/* 
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isEqual from 'lodash.isequal';
import {
  AsyncTaskStatus,
  BulkActionType,
  GenericObject,
  JobStatus
} from '@ion/core/src/types/entities';
import {
  RequestTypeLabels
} from '@ion/core/src/types/general';

export const getFeaturesStatus = (item: JobStatus, currentDevice: GenericObject) => {
  if (item.status === AsyncTaskStatus.PARTIAL_SUCCESS || item.status === AsyncTaskStatus.FAILED) {
    const features = item.other?.features;
    const result = {};
    Object.keys(features).forEach(key => {
      result[key] = currentDevice.features[key];
    });
    if (isEqual(features, result)) {
      return AsyncTaskStatus.SUCCESS;
    }
    return AsyncTaskStatus.FAILED;
  }
  return item.status;
};

/**
 * Utility to check for request type and return boolean value.
 * 
 * ```typescript
 * 
 *    import {isRequestType} from '@ion/applications/src/pages/jobstatus/JobStatusUtils';
 * 
 *    const validate = isRequestType(requestType, RequestTypeLabels.USER);
 * 
 * ```
 */
export const isRequestType =
(val: RequestTypeLabels | BulkActionType, type: RequestTypeLabels): boolean => (
  val === RequestTypeLabels[type?.toUpperCase()]
);

/**
 * Utility to check for device request type and return boolean value.
 * 
 * ```typescript
 * 
 *    import {isDeviceRequest} from '@ion/applications/src/pages/jobstatus/JobStatusUtils';
 * 
 *    const validate = isDeviceRequest(requestType);
 * 
 * ```
 */
export const isDeviceRequest = (val: RequestTypeLabels): boolean => (
  isRequestType(val, RequestTypeLabels.DEVICE)
);

/**
 * Utility to check for serviceEnablement actiontype or enableServices request type and return boolean value.
 * 
 * ```typescript
 * 
 *    import {isEnableServicesRequest} from '@ion/applications/src/pages/jobstatus/JobStatusUtils';
 * 
 *    const validate = isEnableServicesRequest(requestName);
 * 
 * ```
 */
export const isEnableServicesRequest = (val: BulkActionType): boolean => (
  isEqual(val, BulkActionType.SERVICE_ENABLEMENT) || isRequestType(val, RequestTypeLabels.ENABLESERVICES)
);
